<script setup>
import { ref, nextTick, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useToggle } from '@vueuse/core';
import { useCopilotStore, getThreadTitle } from 'dashboard/stores/copilot';
import { useCopilotToken } from 'shared/composables/copilot/useCopilotToken';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
});

const emit = defineEmits(['selectThread', 'newThread']);
const { t } = useI18n();
const store = useCopilotStore();
const {
  groupedThreads,
  filteredThreads,
  threadsLoading,
  activeThreadId,
  searchQuery,
  sortBy,
  groupBy,
} = storeToRefs(store);
const searchInputRef = ref(null);
const showDeleteModal = ref(false);
const pendingDeleteId = ref(null);
const actionPending = ref(false);
const [showSortMenu, toggleSortMenu] = useToggle(false);
const [showGroupMenu, toggleGroupMenu] = useToggle(false);

const apiUrl = computed(
  () =>
    window.globalConfig?.COPILOT_URL || import.meta.env.VITE_COPILOT_URL || ''
);
const { getToken } = useCopilotToken(props.accountId);

const authOpts = () => ({ apiUrl: apiUrl.value, getToken });

const sortMenuItems = computed(() => [
  {
    value: 'updated_desc',
    action: 'sort',
    label: t('COPILOT.SORT.UPDATED_DESC'),
    icon: 'i-lucide-arrow-down-wide-narrow',
    isSelected: sortBy.value === 'updated_desc',
  },
  {
    value: 'updated_asc',
    action: 'sort',
    label: t('COPILOT.SORT.UPDATED_ASC'),
    icon: 'i-lucide-arrow-up-narrow-wide',
    isSelected: sortBy.value === 'updated_asc',
  },
  {
    value: 'created_desc',
    action: 'sort',
    label: t('COPILOT.SORT.CREATED_DESC'),
    icon: 'i-lucide-calendar-arrow-down',
    isSelected: sortBy.value === 'created_desc',
  },
  {
    value: 'created_asc',
    action: 'sort',
    label: t('COPILOT.SORT.CREATED_ASC'),
    icon: 'i-lucide-calendar-arrow-up',
    isSelected: sortBy.value === 'created_asc',
  },
]);

const groupMenuItems = computed(() => [
  {
    value: 'date',
    action: 'group',
    label: t('COPILOT.GROUP.DATE'),
    icon: 'i-lucide-calendar-days',
    isSelected: groupBy.value === 'date',
  },
  {
    value: 'assistant',
    action: 'group',
    label: t('COPILOT.GROUP.ASSISTANT'),
    icon: 'i-lucide-sparkles',
    isSelected: groupBy.value === 'assistant',
  },
  {
    value: 'none',
    action: 'group',
    label: t('COPILOT.GROUP.NONE'),
    icon: 'i-lucide-list',
    isSelected: groupBy.value === 'none',
  },
]);

function handleSortAction({ value }) {
  store.setSortBy(value);
  toggleSortMenu(false);
}

function handleGroupAction({ value }) {
  store.setGroupBy(value);
  toggleGroupMenu(false);
}

function focusSearch() {
  nextTick(() => searchInputRef.value?.focus());
}

defineExpose({ focusSearch });

function threadTitle(thread) {
  return getThreadTitle(thread, t('COPILOT.EMPTY_THREAD'));
}

function getGroupLabel(group) {
  if (group.type === 'none') return null;
  if (group.type === 'date') {
    return t(`COPILOT.GROUP_LABELS.${group.key.toUpperCase()}`);
  }
  const assistantKey = String(group.key || 'copilot').toUpperCase();
  const i18nKey = `COPILOT.ASSISTANTS.${assistantKey}.NAME`;
  const label = t(i18nKey);
  return label === i18nKey ? group.key : label;
}

function handleSelect(threadId) {
  emit('selectThread', threadId);
}

async function handleArchive(threadId) {
  if (actionPending.value) return;
  const wasActive = activeThreadId.value === threadId;
  actionPending.value = true;
  try {
    const ok = await store.archiveThread(threadId, authOpts());
    if (!ok) {
      useAlert(t('COPILOT.ARCHIVE_FAILED'));
      return;
    }
    if (wasActive) emit('newThread');
  } finally {
    actionPending.value = false;
  }
}

function openDeleteModal(threadId) {
  pendingDeleteId.value = threadId;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  pendingDeleteId.value = null;
}

async function confirmDelete() {
  const threadId = pendingDeleteId.value;
  closeDeleteModal();
  if (!threadId || actionPending.value) return;

  const wasActive = activeThreadId.value === threadId;
  actionPending.value = true;
  try {
    const ok = await store.deleteThread(threadId, authOpts());
    if (!ok) {
      useAlert(t('COPILOT.DELETE_FAILED'));
      return;
    }
    if (wasActive) emit('newThread');
  } finally {
    actionPending.value = false;
  }
}
</script>

<template>
  <div
    class="flex flex-col h-full w-full lg:min-w-[340px] lg:max-w-[340px] ltr:border-r rtl:border-l border-n-weak"
  >
    <!-- Header -->
    <div class="flex items-center justify-between w-full gap-1 px-4 py-5">
      <div class="flex items-center gap-2 min-w-0 flex-1 h-10">
        <h1 class="min-w-0 text-xl font-medium truncate text-n-slate-12">
          {{ t('COPILOT.TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-1">
        <Button
          faded
          slate
          sm
          icon="i-lucide-x"
          @click="store.closeSidebar()"
        />
      </div>
    </div>

    <!-- Search + sort/group controls -->
    <div class="px-4 pb-3">
      <div class="flex items-center gap-1.5">
        <div class="relative flex-1 min-w-0">
          <span
            class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-5 text-n-slate-9"
          />
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="reset-base w-full !pl-10 pr-3 py-2.5 text-sm rounded-md border border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand !mb-0"
            :placeholder="t('COPILOT.SEARCH_CHATS')"
          />
        </div>

        <div
          v-on-clickaway="() => toggleSortMenu(false)"
          class="relative shrink-0"
        >
          <Button
            faded
            slate
            sm
            icon="i-lucide-arrow-up-down"
            :title="t('COPILOT.SORT.LABEL')"
            @click="
              toggleGroupMenu(false);
              toggleSortMenu();
            "
          />
          <DropdownMenu
            v-if="showSortMenu"
            :menu-items="sortMenuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full min-w-[200px]"
            @action="handleSortAction"
          />
        </div>

        <div
          v-on-clickaway="() => toggleGroupMenu(false)"
          class="relative shrink-0"
        >
          <Button
            faded
            slate
            sm
            icon="i-lucide-layers"
            :title="t('COPILOT.GROUP.LABEL')"
            @click="
              toggleSortMenu(false);
              toggleGroupMenu();
            "
          />
          <DropdownMenu
            v-if="showGroupMenu"
            :menu-items="groupMenuItems"
            class="mt-1 ltr:right-0 rtl:left-0 top-full min-w-[180px]"
            @action="handleGroupAction"
          />
        </div>
      </div>
    </div>

    <!-- Thread list -->
    <div
      class="flex flex-col gap-0.5 w-full flex-1 pb-4 overflow-x-hidden px-2 overflow-y-auto"
    >
      <!-- Loading state -->
      <div v-if="threadsLoading" class="flex items-center justify-center py-8">
        <div class="i-lucide-loader-2 size-6 text-n-slate-11 animate-spin" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!filteredThreads.length"
        class="flex flex-col items-center justify-center py-8 px-4 text-center"
      >
        <div
          class="i-lucide-message-square-dashed size-8 text-n-slate-9 mb-2"
        />
        <p class="text-sm text-n-slate-11">
          {{
            searchQuery
              ? t('COPILOT.NO_RESULTS')
              : t('COPILOT.NO_CONVERSATIONS')
          }}
        </p>
      </div>

      <!-- Grouped thread items -->
      <template v-else>
        <div
          v-for="group in groupedThreads"
          :key="`${group.type}-${group.key}`"
          class="flex flex-col gap-0.5"
        >
          <p
            v-if="getGroupLabel(group)"
            class="px-3 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
          >
            {{ getGroupLabel(group) }}
          </p>

          <div
            v-for="thread in group.threads"
            :key="thread.thread_id"
            class="group relative flex items-start w-full gap-1 p-3 transition-all duration-300 ease-in-out rounded-none hover:rounded-lg hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 cursor-pointer"
            :class="{
              'rounded-lg bg-n-alpha-1 dark:bg-n-alpha-3':
                thread.thread_id === activeThreadId,
            }"
            @click="handleSelect(thread.thread_id)"
          >
            <div class="flex flex-col flex-1 min-w-0 gap-1 pr-16">
              <p class="text-sm text-n-slate-12 line-clamp-2 mb-0">
                {{ threadTitle(thread) }}
              </p>
            </div>

            <div
              class="absolute right-2 top-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
            >
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors"
                :title="t('COPILOT.ARCHIVE_CHAT')"
                :disabled="actionPending"
                @click.stop="handleArchive(thread.thread_id)"
              >
                <span class="i-lucide-archive size-4" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center size-8 rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-solid-3 transition-colors"
                :title="t('COPILOT.DELETE_CHAT')"
                :disabled="actionPending"
                @click.stop="openDeleteModal(thread.thread_id)"
              >
                <span class="i-lucide-trash-2 size-4" />
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
      :title="t('COPILOT.DELETE_CHAT_TITLE')"
      :message="t('COPILOT.DELETE_CHAT_MESSAGE')"
      :confirm-text="t('COPILOT.DELETE_CHAT_CONFIRM')"
      :reject-text="t('COPILOT.DELETE_CHAT_CANCEL')"
    />
  </div>
</template>
